import { FormControl, FormLabel, Select, Option, FormHelperText } from '@mui/joy';
import { useEffect, useMemo, useState } from 'react';
import { useFeedback } from './feedback/Service';
import { useQuery } from '@apollo/client';
import POSITIONS_QUERY from '../gql/admin/positions.query';

export const emptyRoleplay = () => ({
  title: '',
  candidateRole: '',
  buyerPersona: '',
  context: '',
  buyerBackground: '',
  candidateObjective: ''
});

export const parseQuestions = (dq) => {
  const raw = dq.questions || [];
  if (dq.section?.questionType === 'Roleplay') {
    return raw.map(q => {
      if (typeof q === 'string') {
        try {
          const obj = JSON.parse(q);
          return typeof obj === 'object' && obj !== null ? obj : emptyRoleplay();
        } catch {
          return emptyRoleplay();
        }
      }
      if (typeof q === 'object' && q !== null) return q;
      return emptyRoleplay();
    });
  }
  // Non-roleplay: keep as strings
  return raw.map(q => (typeof q === 'string' ? q : ''));
};

const transformPosition = (position) => {
  // position.defaultQuestions: [{ sectionId, section{ name, maxQuestions, questionType }, questions: [string|json] }]
  const questions = (position.defaultQuestions || []).map(dq => ({
    sectionId: dq.sectionId,
    sectionTitle: dq.section?.name,
    maxQuestions: dq.section?.maxQuestions || (dq.questions?.length || 0),
    questionType: dq.section?.questionType,
    questions: parseQuestions(dq)
  }));
  return {
    id: position._id,
    title: position.title,
    questions
  };
};

const buildTemplates = (positions) => (positions || []).map(p => transformPosition(p));

const DefaultInterviewQuestionsSelector = ({
  interviewQuestions,
  setInterviewQuestions,
  showHelperText = true
}) => {
  const feedback = useFeedback();
  const { data, loading } = useQuery(POSITIONS_QUERY, { variables: { limit: 200 } });

  // Build templates memoized from server data
  const templates = useMemo(() => buildTemplates(data?.positions || []), [data]);

  // Determine if current questions match a template or are custom
  const currentTemplate = useMemo(() => templates.find(
    t => JSON.stringify(t.questions) === JSON.stringify(interviewQuestions)
  ), [templates, interviewQuestions]);

  const [selectedTemplate, setSelectedTemplate] = useState(currentTemplate?.id);

  // Sync selectedTemplate when currentTemplate changes
  useEffect(() => {
    if (currentTemplate) {
      setSelectedTemplate(currentTemplate.id);
    }
  }, [currentTemplate]);

  // Check if there are actual custom questions (content beyond empty strings/fields)
  const hasCustomQuestions = useMemo(() => !currentTemplate && interviewQuestions.some(section => {
    if (!section.questions || section.questions.length === 0) return false;
    return section.questions.some(q => {
      if (typeof q === 'string') return q.trim() !== '';
      if (typeof q === 'object') return Object.values(q).some(val => typeof val === 'string' && val.trim() !== '');
      return false;
    });
  }), [currentTemplate, interviewQuestions]);

  const handleTemplateChange = async (_, value) => {
    if (!value) return;
    if (hasCustomQuestions) {
      const confirmed = await feedback.confirm({
        title: 'Changing the default interview questions will reset any custom changes. Do you want to continue?'
      });
      if (!confirmed) return;
    }
    setSelectedTemplate(value);
    const found = templates.find(t => t.id === value) || templates[0];
    if (found) {
      setInterviewQuestions(found.questions.map(sec => ({ ...sec, questions: [...sec.questions] })));
    }
  };

  // Auto-select first template if none selected and interviewQuestions empty
  useEffect(() => {
    if (!interviewQuestions?.length && templates.length && !selectedTemplate) {
      const first = templates[0];
      setSelectedTemplate(first.id);
      setInterviewQuestions(first.questions.map(sec => ({ ...sec, questions: [...sec.questions] })));
    }
  }, [templates, interviewQuestions, selectedTemplate, setInterviewQuestions]);

  return (
    <FormControl>
      <FormLabel htmlFor="select-default-questions-button">
        Set Default Interview Questions
      </FormLabel>
      <Select
        value={selectedTemplate || ''}
        onChange={handleTemplateChange}
        disabled={loading || !templates.length}
        placeholder={loading ? 'Loading templates…' : 'Select a template'}
        slotProps={{
          button: {
            id: 'select-default-questions-button',
            'aria-labelledby': 'select-default-questions-button'
          }
        }}
      >
        {templates.map(t => (
          <Option key={t.id} value={t.id}>{t.title}</Option>
        ))}
      </Select>
      {showHelperText && (
        <FormHelperText>
          You&apos;ll be able to edit the questions later.
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default DefaultInterviewQuestionsSelector;
